/** Чистые расчёты «Аналитики Ozon»: дельты, доли, тон дельты, метки узлов дерева, база доли.
 *
 *  Знаки. В витрине (и в ответах API) выручка +, удержания и себестоимость −, маржа = выручка +
 *  расходы (`expenses` = удержания + себестоимость). Показываем иначе: удержания и расходы —
 *  положительным числом (`shown(x, "expense")` = −x); компенсация внутри удержаний — с минусом
 *  («уменьшает расходы»). Выручка, маржа, штуки — как
 *  в витрине. Δ и доли считаются по ПОКАЗАННОМУ числу, тон — по смыслу (`deltaTone(…, kind)`):
 *  у дохода (выручка, маржа, шт.) рост — хорошо, у расхода рост — плохо.
 */
import type { SupplyBy, TreeFlags, TreeMetrics } from "@/api/ozonAnalytics";
import { int, NBSP } from "./format";

/** Порог «есть выручка»: меньше полтинника — шум округлений, не база для долей. */
export const REV_EPS = 0.5;

/** Род суммы: доход (выручка, маржа, шт.) или расход (удержания, себестоимость, расходы). */
export type Kind = "income" | "expense";

/** Код группы выручки; все прочие группы P&L — удержания. */
export const REVENUE_GRP = "rev";

/** Род группы P&L по её коду. */
export function grpKind(grp: string): Kind {
  return grp === REVENUE_GRP ? "income" : "expense";
}

/** Показанное значение из суммы витрины: расход — с обратным знаком (удержание −100 → 100). */
export function shown(amount: number, kind: Kind): number {
  return kind === "expense" ? -amount : amount;
}

/** Показанный расход меньше нуля — компенсация, уменьшает расходы (подсказка у значения). */
export const EXPENSE_NEG_TITLE = "уменьшает расходы";

/** Подсказка к показанному значению: у отрицательного расхода — «уменьшает расходы». */
export function shownTitle(value: number, kind: Kind): string | null {
  return kind === "expense" && value < -0.5 ? EXPENSE_NEG_TITLE : null;
}

export interface Delta {
  /** cur − cmp, в единицах значения (₽, шт.) */
  abs: number;
  /** относительное изменение величины: (cur − cmp) / cmp; null — не определено */
  rel: number | null;
}

/** Δ к периоду сравнения; null — сравнения нет. Аргументы — ПОКАЗАННЫЕ значения (`shown`).
 *
 *  Расходы 100 → 150: abs +50, rel +50% (тон расхода — плохо). rel делится на cmp со знаком;
 *  при смене знака (маржа −100 → +50) и при cmp = 0 относительная Δ не имеет смысла — null
 *  («—»), абсолютная остаётся. */
export function delta(cur: number, cmp: number | null | undefined): Delta | null {
  if (cmp === null || cmp === undefined) return null;
  const abs = cur - cmp;
  const flipped = (cur > 0 && cmp < 0) || (cur < 0 && cmp > 0);
  // знак % — как у Δ ₽: у отрицательной базы (маржа узла-расхода) делим на модуль
  const rel = cmp === 0 || flipped ? null : abs / Math.abs(cmp);
  return { abs, rel };
}

/** Доля суммы в выручке (передавать показанное значение: доля расхода положительная).
 *  Нет выручки — null. */
export function share(value: number, revenue: number | null | undefined): number | null {
  if (revenue === null || revenue === undefined || revenue <= REV_EPS) return null;
  return value / revenue;
}

/** Разница долей (п.п. в долях): null, если любой из долей нет. */
export function shareDelta(cur: number | null, cmp: number | null): number | null {
  if (cur === null || cmp === null) return null;
  return cur - cmp;
}

export type Tone = "good" | "bad" | "neutral";

/** Тон дельты по смыслу: у дохода рост — хорошо, у расхода (и его доли выручки) рост — плохо.
 *  abs — Δ показанных значений; eps — порог шума (для ₽ — полрубля, для долей — 0,05 п.п.). */
export function deltaTone(
  abs: number | null | undefined,
  eps = 0.5,
  kind: Kind = "income",
): Tone {
  if (abs === null || abs === undefined || Math.abs(abs) < eps) return "neutral";
  const up = abs > 0;
  return up === (kind === "income") ? "good" : "bad";
}

export const TONE_CLASS: Record<Tone, string> = {
  good: "text-emerald-700 dark:text-emerald-400",
  bad: "text-red-600 dark:text-red-400",
  neutral: "text-muted-foreground",
};

/** Метки узла дерева «распределено» / «общие».
 *
 *  Ставятся, только когда ВСЯ сумма узла привязана не напрямую (в узле нет ни одной строки
 *  `прямое`): иначе «распределено» висело бы почти на каждом артикуле — у любого есть доля
 *  хранения или кросс-докинга. В измерении «способ привязки» метки не нужны — способ и есть
 *  подпись узла. */
export function nodeMarks(
  flags: TreeFlags,
  dim: string,
): { alloc: boolean; common: boolean } {
  if (dim === "method" || flags.has_direct) return { alloc: false, common: false };
  return { alloc: flags.has_alloc, common: flags.has_common };
}

/** База доли: выручка ближайшего предка, у которого она есть. */
export interface ShareBase {
  rev: number;
  name: string;
}

/** База для детей узла: свой узел, если у него есть выручка, иначе база родителя. */
export function childBase(
  metrics: TreeMetrics | null,
  name: string,
  parent: ShareBase | null,
): ShareBase | null {
  if (metrics && metrics.rev > REV_EPS) return { rev: metrics.rev, name };
  return parent;
}

/** Кросс-док ₽/шт заявки (подпись под суммой кросс-дока на вкладке «Поставки»). */
export type XdPerUnit =
  /** |начисления кросс-дока| / принятые штуки всей заявки */
  | { status: "ok"; perUnit: number }
  /** у части (или всех) поставок заявки нет факта штук — делить не на что, ₽/шт был бы завышен */
  | { status: "no_fact"; missing: number; total: number }
  /** по факту принято 0 шт. */
  | { status: "zero_fact" };

/** Кросс-док ₽/шт заявки: |начисления кросс-дока| / принятые штуки всей заявки — та же база, что у
 *  диагноза «Дорогой кросс-докинг» (supply_diag: xd_units = Σ fact_units поставок заявки).
 *
 *  `factUnits` строки бэк суммирует только по поставкам С фактом (`_sum_or_none`), поэтому смотрим
 *  на сами поставки: хоть у одной `fact_units === null` — не считаем (`no_fact`), иначе знаменатель
 *  без недостающих штук молча завысил бы ₽/шт. Заявка без поставок в диагнозе — тоже `no_fact`
 *  (0 из 0). null — кросс-дока нет (|xd| < 0,5 ₽). */
export function xdPerUnit(
  xd: number,
  factUnits: number | null,
  supplyFacts: readonly (number | null)[],
): XdPerUnit | null {
  if (Math.abs(xd) < 0.5) return null;
  const missing = supplyFacts.filter((f) => f === null).length;
  if (missing > 0 || factUnits === null) {
    return { status: "no_fact", missing, total: supplyFacts.length };
  }
  if (factUnits < 0) {
    throw new Error(`поставки: факт штук заявки отрицательный (${factUnits})`);
  }
  if (factUnits === 0) return { status: "zero_fact" };
  return { status: "ok", perUnit: Math.abs(xd) / factUnits };
}

/** Подпись и подсказка ₽/шт. В режиме «по месяцу начисления» кросс-док — только начисления
 *  периода, а штуки — всей заявки: число показываем, но в подсказке это сказано явно. */
export function xdPerUnitLabel(x: XdPerUnit, by: SupplyBy): { text: string; title: string } {
  if (x.status === "zero_fact") {
    return { text: "0 шт. по факту", title: "По акту принято 0 шт. — ₽/шт не определён" };
  }
  if (x.status === "no_fact") {
    if (x.total === 0) {
      return { text: "нет факта шт.", title: "Заявки нет в диагнозе поставок — принятых штук нет" };
    }
    const title = `Без акта приёмки: ${x.missing} из ${x.total} поставок — ₽/шт не считаем, иначе он был бы завышен`;
    return {
      text: x.missing === x.total ? "нет факта шт." : "нет факта по части поставок",
      title,
    };
  }
  const per = x.perUnit;
  const text = `${per < 10 ? per.toFixed(1).replace(".", ",") : int(per)}${NBSP}₽/шт`;
  return {
    text,
    title:
      by === "invoice"
        ? "Кросс-докинг / принятые штуки заявки (факт по акту) — по начислениям кросс-докинга в периоде; полная стоимость заявки — в режиме „по дате отправки“"
        : "Кросс-докинг заявки / принятые штуки (факт по акту)",
  };
}
